import * as React from "react";
import { FileDown, FileSpreadsheet } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "@/lib/api-base";
import { db } from "@/lib/dexie-db";
import { fetchTraegerOrgs } from "./traeger-api";
import { recentMonths, type ExportPeriod } from "@/lib/stufen-labels";

/**
 * Träger-Export-Route — period-picker + einrichtungs-auswahl + download-buttons (PDF + XLSX).
 *
 * Die downloads gehen direkt gegen die API mit Authorization-header
 * via fetch. Wir nutzen blob() um den download als datei zu triggern,
 * weil <a download>-direct nicht den auth-header mitsendet.
 *
 * Period-API-Contract (GET /v1/traeger/export.{pdf,xlsx}?period=<P>[&org_id=<uuid>]):
 *   month    → laufender Kalendermonat (default)
 *   YYYY-MM  → spezifischer Kalendermonat
 *   quarter  → letztes Quartal (rolling 90d)
 *   year     → letztes Jahr (rolling 365d)
 *   season   → Hitzesaison Jun–Sep
 *   all      → gesamter Zeitraum
 *
 * org_id (optional): ohne = konsolidierter Export aller kind-orgs;
 *   mit = Export nur dieser Einrichtung. Server prüft parent_org_id == träger-org (403 sonst).
 */

type PeriodType = "month" | "quarter" | "year" | "season" | "all";

const PERIOD_OPTIONS: { value: PeriodType; label: string; hint: string }[] = [
  { value: "month",   label: "Monat",   hint: "Aktueller Kalendermonat (1. bis heute)" },
  { value: "quarter", label: "Quartal", hint: "Letztes Quartal (letzte 90 Tage)" },
  { value: "year",    label: "Jahr",    hint: "Letztes Jahr (letzte 365 Tage)" },
  { value: "season",  label: "Saison",  hint: "Hitzesaison Juni–September" },
  { value: "all",     label: "Alles",   hint: "Gesamter Zeitraum (alle Einträge)" },
];

export function TraegerExportRoute(): React.ReactElement {
  const [periodType, setPeriodType] = React.useState<PeriodType>("quarter");
  const [monthValue, setMonthValue] = React.useState<string>("month"); // "month" oder "YYYY-MM"
  const [selectedOrgId, setSelectedOrgId] = React.useState<string>(""); // "" = alle
  const [downloading, setDownloading] = React.useState<"pdf" | "xlsx" | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Kind-Einrichtungen für den Einrichtungs-Select
  const orgs = useQuery({
    queryKey: ["traeger", "orgs"],
    queryFn: fetchTraegerOrgs,
    staleTime: 60_000,
  });

  // Berechnet den API-period-string
  function resolvedPeriod(): ExportPeriod {
    if (periodType === "month") {
      return monthValue as ExportPeriod;
    }
    return periodType;
  }

  async function download(format: "pdf" | "xlsx") {
    setDownloading(format);
    setError(null);
    try {
      const auth = await db.auth.get("current");
      if (!auth) {
        setError("Nicht angemeldet.");
        setDownloading(null);
        return;
      }
      const period = resolvedPeriod();
      let url = `${API_BASE_URL}/v1/traeger/export.${format}?period=${encodeURIComponent(period)}`;
      if (selectedOrgId) {
        url += `&org_id=${encodeURIComponent(selectedOrgId)}`;
      }
      const resp = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${auth.accessToken}` },
      });
      if (!resp.ok) {
        setError(`Server-Fehler: ${resp.status}`);
        setDownloading(null);
        return;
      }
      const blob = await resp.blob();
      const filename = extractFilename(resp.headers.get("Content-Disposition")) ?? `traeger-export.${format}`;
      triggerBrowserDownload(blob, filename);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unbekannter Fehler");
    }
    setDownloading(null);
  }

  const activePeriodHint = PERIOD_OPTIONS.find((o) => o.value === periodType)?.hint ?? "";
  const months = recentMonths(14);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-neutral-900">Konsolidiert exportieren</h1>
        <p className="text-sm text-neutral-600">
          PDF (Landscape, Hessen-Standard) oder Excel (Multi-Sheet) — für alle oder eine Einrichtung.
        </p>
      </header>

      {/* Zeitraum */}
      <fieldset className="rounded-lg border border-neutral-200 bg-white p-4">
        <legend className="px-2 text-sm font-medium text-neutral-700">Zeitraum</legend>
        <div className="flex flex-wrap gap-2">
          {PERIOD_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                periodType === opt.value
                  ? "border-[color:var(--color-warm-orange-700)] bg-[color:var(--color-warm-orange-50)] font-semibold text-[color:var(--color-warm-orange-900)]"
                  : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              <input
                type="radio"
                name="traeger-period"
                value={opt.value}
                checked={periodType === opt.value}
                onChange={() => setPeriodType(opt.value)}
                className="sr-only"
              />
              {opt.label}
            </label>
          ))}
        </div>

        {/* Monats-Select — nur wenn "Monat" aktiv */}
        {periodType === "month" && (
          <div className="mt-3">
            <label
              htmlFor="traeger-export-month"
              className="block text-xs font-medium text-neutral-600 mb-1"
            >
              Kalendermonat
            </label>
            <select
              id="traeger-export-month"
              value={monthValue}
              onChange={(e) => setMonthValue(e.target.value)}
              className="h-10 rounded-md border border-neutral-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-warm-orange-700)]"
            >
              {months.map((m, idx) => (
                <option key={m.value} value={idx === 0 ? "month" : m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-neutral-500">
              Aktueller Monat = laufend (1. des Monats bis heute)
            </p>
          </div>
        )}

        {activePeriodHint && periodType !== "month" && (
          <p className="mt-2 text-xs text-neutral-500">{activePeriodHint}</p>
        )}
      </fieldset>

      {/* Einrichtungs-Auswahl */}
      <fieldset className="rounded-lg border border-neutral-200 bg-white p-4">
        <legend className="px-2 text-sm font-medium text-neutral-700">Einrichtung</legend>
        {orgs.isPending && (
          <div className="h-10 animate-pulse rounded bg-neutral-100" />
        )}
        {orgs.isError && (
          <p className="text-xs text-red-700">Einrichtungsliste konnte nicht geladen werden.</p>
        )}
        {orgs.data && (
          <>
            <label htmlFor="traeger-org-select" className="block text-xs font-medium text-neutral-600 mb-1">
              Einrichtung wählen
            </label>
            <select
              id="traeger-org-select"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="h-10 w-full max-w-sm rounded-md border border-neutral-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-warm-orange-700)]"
            >
              <option value="">Alle Einrichtungen (konsolidiert)</option>
              {orgs.data.items.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                  {org.city ? ` · ${org.city}` : ""}
                </option>
              ))}
            </select>
            {selectedOrgId && (
              <p className="mt-1 text-xs text-neutral-500">
                Export enthält nur Daten dieser Einrichtung.
              </p>
            )}
            {!selectedOrgId && (
              <p className="mt-1 text-xs text-neutral-500">
                Konsolidierter Export über alle {orgs.data.current_count} Einrichtungen.
              </p>
            )}
          </>
        )}
      </fieldset>

      {error && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          role="alert"
          aria-live="assertive"
        >
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => void download("pdf")}
          disabled={downloading !== null}
          className="inline-flex items-center gap-2 rounded-md bg-[color:var(--color-warm-orange-700)] px-4 py-2 text-sm font-medium text-white hover:bg-[color:var(--color-warm-orange-800)] disabled:opacity-50"
        >
          <FileDown className="size-4" aria-hidden="true" />
          {downloading === "pdf" ? "Wird erzeugt …" : "PDF herunterladen"}
        </button>
        <button
          type="button"
          onClick={() => void download("xlsx")}
          disabled={downloading !== null}
          className="inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-neutral-50 disabled:opacity-50"
        >
          <FileSpreadsheet className="size-4" aria-hidden="true" />
          {downloading === "xlsx" ? "Wird erzeugt …" : "Excel herunterladen"}
        </button>
      </div>

      <aside className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-xs text-neutral-600">
        <p>
          Hinweis: Aufbewahrungs-Pflicht für Hitzeprotokolle beträgt 10 Jahre (BGB §195 +
          Verkehrssicherungspflicht). Bewahren Sie das heruntergeladene Dokument entsprechend
          Ihrer internen Doku-Richtlinie auf.
        </p>
        {selectedOrgId && (
          <p className="mt-2 text-amber-700">
            Sicherheitshinweis: Der Server prüft, dass die gewählte Einrichtung zu Ihrem
            Träger-Konto gehört. Ein unbefugter Zugriff auf fremde Daten ist nicht möglich.
          </p>
        )}
      </aside>
    </div>
  );
}

function extractFilename(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;
  const match = contentDisposition.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? null;
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
